import React from 'react';
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type Modifier,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Pencil, Plus, X } from 'lucide-react';
import type { Task, TaskChecklistItem } from '../types';
import type { TasksApi } from '../useTasks';
import { TASK_SECTION_LABEL_CLASS } from './taskUi';

/** Nur senkrecht verschieben. */
const restrictToVerticalAxis: Modifier = ({ transform }) => ({ ...transform, x: 0 });

/** Gedrückt halten startet das Ziehen — ein kurzer Klick bleibt ein Klick. */
const HOLD_TO_DRAG = { delay: 200, tolerance: 5 };

type Props = {
  task: Task;
  canEdit: boolean;
  tasksApi: TasksApi;
};

export function TaskChecklist({ task, canEdit, tasksApi }: Props) {
  const [newCheck, setNewCheck] = React.useState('');
  React.useEffect(() => setNewCheck(''), [task.id]);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: HOLD_TO_DRAG }),
    useSensor(TouchSensor, { activationConstraint: HOLD_TO_DRAG }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const ids = task.checklist.map((c) => c.id);
  /** Zeitpunkt des letzten Drops — der Klick direkt danach darf nicht abhaken. */
  const lastDropAt = React.useRef(0);
  const doneCount = task.checklist.filter((c) => c.done).length;

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    lastDropAt.current = Date.now();
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    void tasksApi.reorderChecklist(task.id, arrayMove(ids, from, to));
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between pb-1">
        <span className={TASK_SECTION_LABEL_CLASS}>Checkliste</span>
        {task.checklist.length > 0 && (
          <span className="text-xs tabular-nums text-app-text-subtle">
            {doneCount} von {task.checklist.length}
          </span>
        )}
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={onDragEnd}
        onDragCancel={() => {
          lastDropAt.current = Date.now();
        }}
      >
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          {task.checklist.map((item) => (
            <ChecklistRow
              key={item.id}
              taskId={task.id}
              item={item}
              canEdit={canEdit}
              tasksApi={tasksApi}
              justDropped={() => Date.now() - lastDropAt.current < 300}
            />
          ))}
        </SortableContext>
      </DndContext>

      {canEdit && (
        <div className="flex h-8 items-center gap-2.5 border-b border-app-border">
          <Plus className="h-4 w-4 shrink-0 text-app-text-subtle" />
          <input
            value={newCheck}
            onChange={(e) => setNewCheck(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter' || e.nativeEvent.isComposing) return;
              const t = newCheck.trim();
              if (!t) return;
              e.preventDefault();
              void tasksApi.addChecklistItem(task.id, t);
              setNewCheck('');
            }}
            placeholder="Punkt hinzufügen"
            aria-label="Checklistenpunkt hinzufügen"
            className="min-w-0 flex-1 bg-transparent text-sm text-app-text outline-none placeholder:text-app-text-subtle"
          />
        </div>
      )}
    </div>
  );
}

function ChecklistRow({
  taskId,
  item,
  canEdit,
  tasksApi,
  justDropped,
}: {
  taskId: string;
  item: TaskChecklistItem;
  canEdit: boolean;
  tasksApi: TasksApi;
  justDropped: () => boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    disabled: !canEdit,
  });
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(item.text);

  const startEdit = () => {
    if (!canEdit) return;
    setDraft(item.text);
    setEditing(true);
  };
  const commit = () => {
    setEditing(false);
    const next = draft.replace(/\s+/g, ' ').trim();
    if (next && next !== item.text) void tasksApi.renameChecklistItem(taskId, item.id, next);
  };

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={`group -mx-1.5 flex min-h-8 items-center gap-2.5 rounded-md px-1.5 text-sm ${
        isDragging ? 'relative z-10 bg-app-surface shadow-md ring-1 ring-app-border' : 'hover:bg-app-hover'
      }`}
    >
      <input
        type="checkbox"
        checked={item.done}
        disabled={!canEdit}
        onChange={() => void tasksApi.toggleChecklistItem(taskId, item.id)}
        aria-label={item.done ? `${item.text} als offen markieren` : `${item.text} abhaken`}
        className="h-4 w-4 shrink-0 cursor-pointer accent-pipedrive-blue"
      />

      {editing ? (
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={(e) => e.currentTarget.select()}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
              e.preventDefault();
              commit();
            } else if (e.key === 'Escape') {
              // Nicht bis zum Panel durchreichen (Esc schließt sonst das Detail).
              e.preventDefault();
              e.nativeEvent.stopImmediatePropagation();
              setEditing(false);
            }
          }}
          aria-label="Punkt umbenennen"
          className="-mx-1 my-0.5 min-w-0 flex-1 rounded border border-pipedrive-blue bg-app-surface px-1 py-0.5 text-sm text-app-text outline-none ring-2 ring-pipedrive-blue/20"
        />
      ) : (
        <span
          {...attributes}
          {...listeners}
          onClick={() => {
            // Klick auf den Text hakt ab wie die Checkbox — außer direkt nach einem Drop.
            if (!canEdit || justDropped()) return;
            void tasksApi.toggleChecklistItem(taskId, item.id);
          }}
          title={canEdit ? 'Klicken zum Abhaken · Gedrückt halten zum Verschieben' : undefined}
          className={`min-w-0 flex-1 select-none py-1 outline-none ${canEdit ? (isDragging ? 'cursor-grabbing' : 'cursor-pointer') : ''} ${
            item.done ? 'text-app-text-subtle line-through' : 'text-app-text-muted'
          }`}
        >
          {item.text}
        </span>
      )}

      {canEdit && !editing && (
        <span className="flex shrink-0 items-center opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100">
          <button
            type="button"
            onClick={startEdit}
            title="Umbenennen"
            aria-label="Punkt umbenennen"
            className="flex h-6 w-6 items-center justify-center rounded text-app-text-subtle hover:text-app-text"
          >
            <Pencil className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={() => void tasksApi.removeChecklistItem(taskId, item.id)}
            title="Punkt entfernen"
            aria-label="Punkt entfernen"
            className="flex h-6 w-6 items-center justify-center rounded text-app-text-subtle hover:text-red-600"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </span>
      )}
    </div>
  );
}
